/* eslint-disable react-hooks/exhaustive-deps */
import React, {FC, createContext, useContext, useEffect, useState} from 'react'
import {WithChildren} from '../../helpers'
import { useLocation, useParams} from 'react-router-dom'
import Layout from './Layout'

export interface PageLink {
  title: string
  path: string
  isActive: boolean
  isSeparator?: boolean
}

export interface PageDataContextModel {
  pageTitle?: string
  setPageTitle: (_title: string) => void
  pageDescription?: string
  setPageDescription: (_description: string) => void
  pageBreadcrumbs?: Array<PageLink>
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {},
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {},
  setPageDescription: (_description: string) => {},
})

const PageDataProvider: FC<WithChildren> = ({children}) => {
  const [pageTitle, setPageTitle] = useState<string>('')
  const [pageDescription, setPageDescription] = useState<string>('')
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([])

  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
  }
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>
}

function usePageData() {
  return useContext(PageDataContext)
}

type Props = {
  description?: string
  breadcrumbs?: Array<PageLink>
  title?: string
  icon?: string
  desc?: string
  cloudType?: string
  cloudId?: string
}

const CloudPageTitle: FC<Props & WithChildren> = ({
  children,
  description,
  breadcrumbs,
  title,
  icon,
  desc,
  cloudType,
  cloudId,
}) => {
  const {setPageTitle, setPageDescription, setPageBreadcrumbs} = usePageData()
  const location = useLocation()
  const {id} = useParams()
  
  const getCloudTabs = () => {
    const currentId = cloudId || id
    
    if (currentId) {
      return [
        {
          title: 'Issues',
          slug: 'issues',
          href: `/vulnerabilities/clouds/${currentId}`,
        },
        {
          title: 'Checks',
          slug: 'checks',
          href: `/vulnerabilities/clouds/${currentId}/checks`,
        }
      ]
    } else {
      return [
        {
          title: 'Overview',
          slug: 'overview',
          href: '/vulnerabilities/clouds',
        },
        {
          title: 'Checks',
          slug: 'checks',
          href: '/vulnerabilities/clouds/checks',
        },
        // {
        //   title: 'Assets',
        //   slug: 'assets',
        //   href: '/clouds/assets',
        // },
        // {
        //   title: 'Containers',
        //   slug: 'containers',
        //   href: '/clouds/containers',
        // }
      ]
    }
  }

  const tabs = getCloudTabs()
  
  const [activeTab, setActiveTab] = useState(
    (cloudId || id) ? 'issues' : 'overview'
  )
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString())
    }
    return () => {
      setPageTitle('')
    }
  }, [children])

  useEffect(() => {
    if (description) {
      setPageDescription(description)
    }
    return () => {
      setPageDescription('')
    }
  }, [description])

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs)
    }
    return () => {
      setPageBreadcrumbs([])
    }
  }, [breadcrumbs])

  useEffect(() => {
    const currentId = cloudId || id
    const currentCloudType = cloudType?.toLowerCase() || 'gcp'
    
    if (location.pathname.includes(`${currentCloudType}/configuration`)) {
      setActiveTab('')
    } else if (currentId) {
      if (location.pathname.includes('checks')) {
        setActiveTab('checks')
      } else if(location.pathname.includes('assets')) {
        setActiveTab('assets')
      } else if(location.pathname.includes('containers')) {
        setActiveTab('containers')
      } else {
        setActiveTab('issues')
      }
    } else {
      if (location.pathname.includes('checks')) {
        setActiveTab('checks')
      } else if(location.pathname.includes('assets')) {
        setActiveTab('assets')
      } else if(location.pathname.includes('containers')) {
        setActiveTab('containers')
      } else {
        setActiveTab('overview')
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, cloudId, cloudType, location.pathname])


  return (
    <>
    {icon ? (
      <Layout
        icon={icon}
        title={title}
        desc={desc}
        tabs={tabs}
        activeTab={activeTab}
      />
    ) : null}
  </>
  )
}

const PageDescription: FC<WithChildren> = ({children}) => {
  const {setPageDescription} = usePageData()
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString())
    }
    return () => {
      setPageDescription('')
    }
  }, [children])
  return <></>
}

export {PageDescription, CloudPageTitle, PageDataProvider, usePageData}
